import { ILanguageOption } from 'shared/interfaces/language.interface';
import LanguageService from 'shared/services/LanguageService';

export const DEFAULT_LANGUAGE = 'en';

export const FALLBACK_LANGUAGES: ILanguageOption[] = [
  { code: DEFAULT_LANGUAGE, label: 'English', locale: 'en-US' }
];

const LANGUAGE_CODE_PATTERN = /^[a-z]{2,3}$/;

export const toBaseCode = (value?: string | null): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const code = value.trim().toLowerCase().split(/[-_]/)[0];
  return LANGUAGE_CODE_PATTERN.test(code) ? code : undefined;
};

export const getSupportedLanguages = (
  supportedLanguages?: string[] | null
): string[] | null => {
  if (!Array.isArray(supportedLanguages)) return null;
  const codes = supportedLanguages
    .map(toBaseCode)
    .filter((code): code is string => !!code);
  return Array.from(new Set([DEFAULT_LANGUAGE, ...codes]));
};

interface IResolveLanguageParams {
  urlLanguage?: string | null;
  userLanguage?: string | null;
  storedLanguage?: string | null;
  affiliateLanguage?: string | null;
  supportedLanguages?: string[] | null;
}

export const resolveLanguage = ({
  urlLanguage,
  userLanguage,
  storedLanguage,
  affiliateLanguage,
  supportedLanguages
}: IResolveLanguageParams): string => {
  const supported = getSupportedLanguages(supportedLanguages);

  if (supported?.length === 1) return supported[0];

  const candidates = [
    urlLanguage,
    userLanguage,
    storedLanguage,
    affiliateLanguage
  ].map(toBaseCode);

  const match = candidates.find(
    (code) => code && (!supported || supported.includes(code))
  );

  return match || DEFAULT_LANGUAGE;
};

export const filterSupportedLanguages = (
  registry: ILanguageOption[],
  supportedLanguages?: string[] | null
): ILanguageOption[] => {
  const supported = getSupportedLanguages(supportedLanguages);
  return supported
    ? registry.filter(({ code }) => supported.includes(code))
    : registry;
};

let registryRequest: Promise<ILanguageOption[]> | null = null;

export const loadLanguageRegistry = (): Promise<ILanguageOption[]> => {
  if (!registryRequest) {
    registryRequest = LanguageService.getLanguages()
      .then((languages) =>
        Array.isArray(languages) && languages.length
          ? languages
          : FALLBACK_LANGUAGES
      )
      .catch(() => {
        registryRequest = null;
        return FALLBACK_LANGUAGES;
      });
  }
  return registryRequest;
};
